/*!
 * Spart.js - SPA Art
 * A lightweight raw JavaScript library for building web user interfaces.
 *
 * (c) 2025 Rhyscitlema
 * Released under the MIT License
 */

export { }; // expose the typedef to other modules

/**
 * @typedef {Object} HTMLElementInfo
 * @property {HTMLElement} [element] - The already created element to be returned right away
 * @property {string} [svg] - An SVG string to create an SVG element - must contain the `xmlns` attribute
 * @property {string} [tag] - If not specified, it will be inferred from the parent element
 * @property {string} [text] - For the text content of the element
 * @property {string} [html] - For the inner HTML of the element
 * @property {Record<string, string|number|boolean|null>} [props] - Properties to set on the element
 * @property {Record<string, string|number|boolean|null>} [attri] - Attributes to set on the element
 * @property {HTMLElementInfo[]} [content] - Inner content to append to the element
 * @property {Record<string, EventListenerOrEventListenerObject>} [events] - Event listeners to add to the element
 * @property {(elem: HTMLElement, info: HTMLElementInfo, parent: HTMLElement) => void} [callback] - A callback function to execute after the element is created or updated
 * @property {string} [id] - The id attribute to set on the element
 * @property {string} [class] - The class attribute to set on the element
 * @property {string} [style] - The style attribute to set on the element
 * @property {string} [role] - The role attribute to set on the element
 * @property {string} [title] - The title attribute to set on the element
 * @property {string} [value] - The value attribute to set on the input element
 * @property {string} [placeholder] - The placeholder attribute to set on the input element
 * @property {true|null} [hidden] - Whether to hide the element (sets the `hidden` attribute)
 */

import { tl, i18nSupported, fetchLanguageTranslations } from "./i18n.js";

/**
 * @typedef {Object} SPAConfig
 * @property {boolean} [isProgressiveWebApp]
 */

/** @type {SPAConfig} */
let spaConfig = {};
export const getConfig = () => spaConfig;

function makeBodyHeightMatchViewPort() {
	// Make the body height to match the visual viewport height
	window.visualViewport.addEventListener('resize', () => {
		const vh = window.visualViewport.height;
		const bh = document.body.offsetHeight;
		const diff = bh - vh;
		if (diff < -1 || diff > 1) {
			document.body.style.height = `${vh}px`;
			console.debug(`Body height adjusted: ${bh} - ${vh} = ${diff}`);
		}
	});
}

/**
 * @param {SPAConfig} config
 */
export async function setup(config) {
	if (config) {
		spaConfig = config;

		if (config.isProgressiveWebApp) {
			setTimeout(makeBodyHeightMatchViewPort, 1000);
		}
	}
	await fetchLanguageTranslations(null);
}

function guessTagFromParent(info, parent) {
	let tag = null;
	const p = parent?.tagName;

	if (p == 'SELECT') {
		tag = 'OPTION';
	}
	else if (p == 'UL' || p == 'OL') {
		tag = 'LI';
	}
	else if (p == 'TR') {
		tag = 'TD';
	}
	else if (!info.text) {
		console.warn('Cannot guess the element tag for:', info);
		tag = 'SPAN';
	}
	return tag;
}

/**
 * A simple and efficient way to create HTML elements.
 * It can create elements with attributes, properties, inner content, and event listeners.
 *
 * @param {HTMLElementInfo} info
 * @param {HTMLElement} [parent]
 * @returns {HTMLElement|null} Element created, or null if creation failed
 */
export function createElement(info, parent) {
	if (!info) return null;
	if (info.element) return info.element;
	if (info.svg) return createSVGElement(info.svg);
	try {
		if (!info.tag)
			info.tag = guessTagFromParent(info, parent);

		if (!info.tag) {
			return document.createTextNode(info.text);
		}

		const elem = document.createElement(info.tag);
		updateElement(elem, info, parent);
		return elem;
	}
	catch (error) {
		console.error(error);
		return null;
	}
}

const excludedKeys = [
	'tag', 'html', 'text', 'title', 'placeholder',
	'attri', 'props', 'content', 'events', 'callback'
];

/**
 * A simple and efficient way to update an element.
 * It can set attributes, properties, inner content, and event listeners.
 *
 * @param {HTMLElement} elem
 * @param {HTMLElementInfo} info
 * @param {HTMLElement} [parent]
 */
export function updateElement(elem, info, parent) {
	if (!elem) return;

	if (info.html !== undefined) {
		elem.innerHTML = info.html;
	}

	if (info.text !== undefined) {
		const text = info.text ?? "";
		elem.textContent = tl(text);
		if (i18nSupported)
			elem.dataset.i18nText = text;
	}

	const title = info.title;
	const placeholder = info.placeholder;

	if (title !== undefined) {
		if (title !== null) {
			elem.setAttribute('title', tl(title));
			if (i18nSupported)
				elem.dataset.i18nTitle = title;
		}
		else {
			elem.removeAttribute('title');
			if (i18nSupported)
				delete elem.dataset.i18nTitle;
		}
	}

	if (placeholder !== undefined) {
		if (placeholder !== null) {
			elem.setAttribute('placeholder', tl(placeholder));
			if (i18nSupported)
				elem.dataset.i18nPlaceholder = placeholder;
		}
		else {
			elem.removeAttribute('placeholder');
			if (i18nSupported)
				delete elem.dataset.i18nPlaceholder;
		}
	}

	if (!info.attri)
		info.attri = {};

	// add attributes
	Object.entries(info).forEach(([key, value]) => {
		if (!excludedKeys.includes(key)) {
			info.attri[key] = value?.toString();
		}
	});

	// set attributes
	if (info.attri) {
		Object.entries(info.attri).forEach(([key, value]) => {
			if (value !== undefined && value !== null)
				elem.setAttribute(key, value.toString());
			else
				elem.removeAttribute(key);
		});
	}

	// add inner content
	if (info.content) {
		if (!info.html)
			elem.innerHTML = ""; // remove existing content

		info.content.forEach(item => {
			const childElement = createElement(item, elem);
			if (childElement) elem.appendChild(childElement);
		});
	}

	// set properties, should come after inner content
	if (info.props) {
		Object.entries(info.props).forEach(([key, value]) => {
			elem[key] = value;
		});
	}

	// add event listeners
	if (info.events) {
		Object.entries(info.events).forEach(([name, handler]) => {
			elem.addEventListener(name, handler);
		});
	}

	if (info.callback) {
		info.callback(elem, info, parent);
	}
}

/**
 * Converts an SVG string into an SVG element.
 * The SVG string must contain the `xmlns` attribute.
 * @param {string} svgString
 * @returns {HTMLElement|null}
 */
export function createSVGElement(svgString) {
	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(svgString, "image/svg+xml");
		const svgElem = doc.documentElement;
		if (svgElem.nodeName === "parsererror")
			throw new Error("Error parsing SVG string");
		return svgElem;
	}
	catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * @param {Object<string, Function>} funcs
 */
export function exposeFunctions(funcs) {
	if (!window)
		return;

	if (!("spart" in window))
		window["spart"] = {};

	Object.entries(funcs).forEach(x => {
		window["spart"][x[0]] = x[1];
	});
}

exposeFunctions({ createElement, updateElement });

